import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetMailOldThreadCandidatesSummary,
  getGetMailOldThreadCandidatesSummaryQueryKey,
  type MailOldThreadSummaryAccountsItem,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Sparkles, ShieldCheck, Inbox } from "lucide-react";

const STORAGE_KEY = "onenext.firstsync-recap.shown";

function readShown(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function markShown(accountId: string) {
  if (typeof window === "undefined") return;
  try {
    const next = { ...readShown(), [accountId]: true };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export default function FirstSyncRecapDialog() {
  const qc = useQueryClient();
  const { data } = useGetMailOldThreadCandidatesSummary({
    query: {
      queryKey: getGetMailOldThreadCandidatesSummaryQueryKey(),
      refetchInterval: 5 * 60_000,
    },
  });

  const accounts = (data?.accounts ?? []) as MailOldThreadSummaryAccountsItem[];
  const eligible = accounts.find(
    (a) => a.firstSyncCompletedAt && a.pendingCount > 0,
  );

  const [open, setOpen] = useState(false);
  const [shownAccountId, setShownAccountId] = useState<string | null>(null);

  useEffect(() => {
    if (!eligible) return;
    const shown = readShown();
    if (shown[eligible.accountId]) return;
    setShownAccountId(eligible.accountId);
    setOpen(true);
  }, [eligible]);

  function handleClose() {
    if (shownAccountId) {
      markShown(shownAccountId);
      qc.invalidateQueries({
        queryKey: getGetMailOldThreadCandidatesSummaryQueryKey(),
      });
    }
    setOpen(false);
  }

  if (!eligible) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent
        className="max-w-lg"
        data-testid="dialog-first-sync-recap"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <DialogTitle>Première synchro terminée</DialogTitle>
          </div>
          <DialogDescription className="text-base text-foreground/90">
            J&apos;ai analysé seulement les mails récents pour ne rien casser.
            <br />
            <span className="font-medium text-foreground">
              {eligible.pendingCount} ancien
              {eligible.pendingCount > 1 ? "s" : ""} fil
              {eligible.pendingCount > 1 ? "s" : ""}
            </span>{" "}
            attende{eligible.pendingCount > 1 ? "nt" : ""} ton avis dans
            « Anciens fils à revoir » — décide ensemble si on les garde, on les
            ignore ou on en fait un projet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
            <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Pas de surprise</p>
              <p className="text-xs text-muted-foreground">
                Aucun projet ni tâche n&apos;a été créé automatiquement à partir
                des vieux mails. Toi seul décides.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
            <Inbox className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">À toi de jouer</p>
              <p className="text-xs text-muted-foreground">
                Ouvre les Paramètres pour parcourir le récap. Tu peux aussi le
                retrouver à tout moment depuis l&apos;icône d&apos;engrenage.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            onClick={handleClose}
            data-testid="button-recap-later"
          >
            Plus tard
          </Button>
          <Link href="/app/settings" onClick={handleClose}>
            <Button data-testid="button-recap-go-settings">
              Voir les anciens fils
            </Button>
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
